/**
/**
 * Author: Mariam Bawa (mariam.bawa@mpac.ca)
 * Date Created: April 18, 2024
 * Context: Utility function created to generate table content for income attributes. Works with objects of type incomeAttributeLookupObject
 */

import React from "react";
import { useTranslation } from "react-i18next";
import Asterisk from "../../components/common/Asterisk/Asterisk";
import { getFormErrorMessageByName, hasError, useFormErrorContext } from "../context/FormErrorContext";
import AIRPNumberInput from "../../components/common/AIRPNumberInput/AIRPNumberInput";
import ToolTip from "../../components/common/ToolTip/ToolTip";
import { useShowIncomeLabel } from "../ShowIncomeLabelContext";
import Input from "../../components/common/Input/Input";
import { shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../previousIncomeAttributes";

interface AttributeTableFragmentProps {
  name: string,
  data: string | number | null,
  isRequiredField: boolean,
  translationFile: string,
  valueChangeHandler?: (value: number | null | undefined, name: string, lastValue: number | undefined | null) => void,
  stringValueChangeHandler?: (value: string | null | undefined, name: string) => void,
  errors?: Record<string, string>,
  symbol?: string,
  readOnly?: boolean,
  fieldType?: "integer" | "string" | "currency" | "decimal",
  hideTooltip?: boolean
}

export default function ProfileAttributeTableFragmentGenerator(props: AttributeTableFragmentProps): JSX.Element {
  const { t } = useTranslation(props.translationFile);
  const { name, data, isRequiredField } = props;

  const { errors } = useFormErrorContext()
  const { showIncomeLabel } = useShowIncomeLabel()

  const renderInput = () => {
    switch (props.fieldType) {
      case "integer":
        return <AIRPNumberInput
          textAlign="right"
          name={name}
          initialValue={data && data}
          decimals={false}
          onChange={(value, lastValue) => {
            props.valueChangeHandler?.(value, name, lastValue)
          }} />
      case "string":
        return <Input name={name} id={name} type="text"
          disabled={props.readOnly}
          onChange={(e) => props.stringValueChangeHandler?.(e.target.value.trim(), name)}
          value={data ? String(data) : null}
          errorMessage={getFormErrorMessageByName(errors, props.name)}
        />
      case "currency":
        return <AIRPNumberInput
          name={name}
          symbol='$'
          textAlign='right'
          initialValue={data && data}
          onChange={(value, lastValue) => {
            props.valueChangeHandler?.(value, name, lastValue)
          }} />
      case "decimal":
        return <AIRPNumberInput
          formatType="decimal"
          decimalPlaces={2}
          textAlign="right"
          name={name}
          initialValue={data && data}
          onChange={(value, lastValue) => {
            props.valueChangeHandler?.(value, name, lastValue)
          }} />
      default:
        return <AIRPNumberInput
          formatType={props.symbol === '$' ? "currency" : "decimal"}
          textAlign="right"
          name={name}
          initialValue={data && data}
          onChange={(value: number | null, lastValue: number | null) => props.valueChangeHandler?.(value, name, lastValue)}
          symbol={props.symbol} />
    }
  }

  const header = props.translationFile === "rounds_rate" ? t(`headers.${name.substring(0, name.length - 1)}`) : t(`headers.${name}`)
  const toolTip = props.translationFile === "rounds_rate" ? t(`tooltip.${name.substring(0, name.length - 1)}`, { defaultValue: '' }) : t(`tooltip.${name}`, { defaultValue: '' })

  return (
    <tr>
      {showIncomeLabel && <td className={"label" + ((shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? " narrow-label" : "")} headers="attribute-label">
        {(!props.hideTooltip && toolTip) ?
          <ToolTip contents={header}>
            {toolTip}
          </ToolTip>
          :
          <>
            {header}
          </>
        }
        {isRequiredField && <>&nbsp;<Asterisk /></>}
      </td>}
      <td className={hasError(errors, props.name) ? "value table-error" : "value"} headers="attribute-value">
        {renderInput()}
      </td>
    </tr>
  );
}